/**
 * Discover Routes — Public browsable content pages
 * Most are guest-accessible, some have auth-gated actions
 */
import React, { Suspense } from 'react';
import { Route } from 'react-router-dom';

// Direct imports (commonly visited)
import Events from '../pages/Events';
import EventDetail from '../pages/EventDetail';
import TrustedProviders from '../pages/TrustedProviders';
import TrustedProvidersCity from '../pages/TrustedProvidersCity';
import Forums from '../pages/Forums';
import Marketplace from '../pages/Marketplace';
import ItemDetail from '../pages/ItemDetail';
import ShopDetail from '../pages/ShopDetail';
import Services from '../pages/Services';
import ServiceDetail from '../pages/ServiceDetail';
import DogParks from '../pages/DogParks';
import DogParkDetail from '../pages/DogParkDetail';
import Breeds from '../pages/Breeds';
import Research from '../pages/Research';
import Organizations from '../pages/Organizations';
import PartnerDirectory from '../pages/PartnerDirectory';
import KennelClubs from '../pages/KennelClubs';
import Rankings from '../pages/Rankings';
import Vendors from '../pages/Vendors';
import ExploreMap from '../pages/MapView';
import SportsEvents from '../pages/SportsEvents';
import SocialChannels from '../pages/SocialChannels';
import PartnerDeals from '../pages/PartnerDeals';
import PartnerPromoPage from '../pages/PartnerPromoPage';
import Recommendations from '../pages/Recommendations';
import Elections from '../pages/Elections';
import Blog from '../pages/Blog';
import BlogPost from '../pages/BlogPost';
import FeedDirectory from '../pages/FeedDirectory';
import DogHealth from '../pages/DogHealth';
import Matchmaking from '../pages/Matchmaking';
import AllergenGenetics from '../pages/AllergenGenetics';
import ConsumerReports from '../pages/ConsumerReports';
import DigitalContent from '../pages/DigitalContent';
import JobBoard from '../pages/JobBoard';
import UserSubmissions from '../pages/UserSubmissions';
import SuggestContent from '../pages/SuggestContent';
import SubGroups from '../pages/SubGroups';
import SubGroupDetail from '../pages/SubGroupDetail';
import Groups from '../pages/Groups';
import Catalog from '../pages/Catalog';
import Profile from '../pages/Profile';
import PostDetail from '../pages/PostDetail';
import MemberPromoPage from '../pages/MemberPromoPage';
import PaymentSuccess from '../pages/PaymentSuccess';
import IdentityComplete from '../pages/IdentityComplete';
import MediaAlbums from '../pages/MediaAlbums';

// Lazy imports (less commonly visited)
const AdoptionAgencies = React.lazy(() => import('../pages/AdoptionAgencies'));
const AdoptablePets = React.lazy(() => import('../pages/AdoptablePets'));
const Videos = React.lazy(() => import('../pages/Videos'));
const AdoptionJourney = React.lazy(() => import('../pages/AdoptionJourney'));
const Memorial = React.lazy(() => import('../pages/Memorial'));
const HallOfFame = React.lazy(() => import('../pages/HallOfFame'));
const RedeemCard = React.lazy(() => import('../pages/RedeemCard'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

export function getDiscoverRoutes(Layout) {
  return [
    // Community
    <Route key="/forums" path="/forums" element={<Layout><Forums /></Layout>} />,
    <Route key="/catalog" path="/catalog" element={<Layout><Catalog /></Layout>} />,
    <Route key="/events" path="/events" element={<Layout><Events /></Layout>} />,
    <Route key="/events/:id" path="/events/:eventId" element={<Layout><EventDetail /></Layout>} />,
    <Route key="/trusted-providers" path="/trusted-providers" element={<Layout><TrustedProviders /></Layout>} />,
    <Route key="/trusted-providers/:citySlug" path="/trusted-providers/:citySlug" element={<Layout><TrustedProvidersCity /></Layout>} />,
    <Route key="/marketplace" path="/marketplace" element={<Layout><Marketplace /></Layout>} />,
    <Route key="/marketplace/:itemId" path="/marketplace/:itemId" element={<Layout><ItemDetail /></Layout>} />,
    <Route key="/shop/:shopId" path="/shop/:shopId" element={<Layout><ShopDetail /></Layout>} />,
    <Route key="/groups" path="/groups" element={<Layout><Groups /></Layout>} />,
    <Route key="/communities" path="/communities" element={<Layout><SubGroups /></Layout>} />,
    <Route key="/communities/:id" path="/communities/:subgroupId" element={<Layout><SubGroupDetail /></Layout>} />,
    <Route key="/post/:postId" path="/post/:postId" element={<Layout><PostDetail /></Layout>} />,
    <Route key="/profile/:username" path="/profile/:username" element={<Layout><Profile /></Layout>} />,
    <Route key="/submissions" path="/submissions" element={<Layout><UserSubmissions /></Layout>} />,
    <Route key="/suggest-content" path="/suggest-content" element={<Layout><SuggestContent /></Layout>} />,
    <Route key="/elections" path="/elections" element={<Layout><Elections /></Layout>} />,

    // Content & Blog
    <Route key="/blog" path="/blog" element={<Layout><Blog /></Layout>} />,
    <Route key="/blog/:postId" path="/blog/:postId" element={<Layout><BlogPost /></Layout>} />,
    <Route key="/feeds" path="/feeds" element={<Layout><FeedDirectory /></Layout>} />,

    // Discover
    <Route key="/services" path="/services" element={<Layout><Services /></Layout>} />,
    <Route key="/services/:serviceId" path="/services/:serviceId" element={<Layout><ServiceDetail /></Layout>} />,
    <Route key="/parks" path="/parks" element={<Layout><DogParks /></Layout>} />,
    <Route key="/parks/:parkId" path="/parks/:parkId" element={<Layout><DogParkDetail /></Layout>} />,
    <Route key="/breeds" path="/breeds" element={<Layout><Breeds /></Layout>} />,
    <Route key="/research" path="/research" element={<Layout><Research /></Layout>} />,
    <Route key="/organizations" path="/organizations" element={<Layout><Organizations /></Layout>} />,
    <Route key="/kennel-clubs" path="/kennel-clubs" element={<Layout><KennelClubs /></Layout>} />,
    <Route key="/partner-program" path="/partner-program" element={<Layout><PartnerDirectory /></Layout>} />,
    <Route key="/rankings" path="/rankings" element={<Layout><Rankings /></Layout>} />,
    <Route key="/vendors" path="/vendors" element={<Layout><Vendors /></Layout>} />,
    <Route key="/map" path="/map" element={<Layout><ExploreMap /></Layout>} />,
    <Route key="/sports" path="/sports" element={<Layout><SportsEvents /></Layout>} />,
    <Route key="/channels" path="/channels" element={<Layout><SocialChannels /></Layout>} />,
    <Route key="/deals" path="/deals" element={<Layout><PartnerDeals /></Layout>} />,
    <Route key="/partners/:slug" path="/partners/:slug" element={<Layout><PartnerPromoPage /></Layout>} />,
    <Route key="/recommendations" path="/recommendations" element={<Layout><Recommendations /></Layout>} />,
    <Route key="/health" path="/health" element={<Layout><DogHealth /></Layout>} />,
    <Route key="/matchmaking" path="/matchmaking" element={<Layout><Matchmaking /></Layout>} />,
    <Route key="/allergens" path="/allergens" element={<Layout><AllergenGenetics /></Layout>} />,
    <Route key="/products" path="/products" element={<Layout><ConsumerReports /></Layout>} />,
    <Route key="/creative" path="/creative" element={<Layout><DigitalContent /></Layout>} />,
    <Route key="/jobs" path="/jobs" element={<Layout><JobBoard /></Layout>} />,
    <Route key="/albums" path="/albums" element={<Layout><MediaAlbums /></Layout>} />,
    <Route key="/members/:username" path="/members/:username" element={<Layout><MemberPromoPage /></Layout>} />,

    // Payment callbacks
    <Route key="/payment/success" path="/payment/success" element={<Layout><PaymentSuccess /></Layout>} />,
    <Route key="/identity/complete" path="/identity/complete" element={<Layout><IdentityComplete /></Layout>} />,

    // Lazy-loaded
    <Route key="/adoption-agencies" path="/adoption-agencies" element={<Layout><Suspense fallback={<PageLoader />}><AdoptionAgencies /></Suspense></Layout>} />,
    <Route key="/adopt" path="/adopt" element={<Layout><Suspense fallback={<PageLoader />}><AdoptablePets /></Suspense></Layout>} />,
    <Route key="/videos" path="/videos" element={<Layout><Suspense fallback={<PageLoader />}><Videos /></Suspense></Layout>} />,
    <Route key="/adoption-journey" path="/adoption-journey" element={<Layout><Suspense fallback={<PageLoader />}><AdoptionJourney /></Suspense></Layout>} />,
    <Route key="/memorial" path="/memorial" element={<Layout><Suspense fallback={<PageLoader />}><Memorial /></Suspense></Layout>} />,
    <Route key="/memorial/:id" path="/memorial/:id" element={<Layout><Suspense fallback={<PageLoader />}><Memorial /></Suspense></Layout>} />,
    <Route key="/hall-of-fame" path="/hall-of-fame" element={<Layout><Suspense fallback={<PageLoader />}><HallOfFame /></Suspense></Layout>} />,
    <Route key="/redeem" path="/redeem" element={<Layout><Suspense fallback={<PageLoader />}><RedeemCard /></Suspense></Layout>} />,
    <Route key="/redeem/:code" path="/redeem/:code" element={<Layout><Suspense fallback={<PageLoader />}><RedeemCard /></Suspense></Layout>} />,
  ];
}
